import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { verifyOtp, resendOtp, updateUnverifiedEmail } from '../services/authService';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/Input';
import Button from '../components/Button';

const Title = styled.h1` margin-bottom: 10px `;
const Subtitle = styled.p` /* ... */ `;
const InfoText = styled.div` /* ... */ `;
const StyledLink = styled(Link)` color: black; text-decoration: underline; display:none; font-weight: 600; &:hover { text-decoration: underline; } `;
const ErrorMessage = styled.p<{ isSuccess?: boolean }>`
  color: ${props => props.isSuccess ? '#4caf50' : '#f44336'};
  margin-top: 5px;
  font-size: 14px;
  text-align: center;
`;
const OtpInputContainer = styled.div` /* ... */ `;
const OtpInput = styled(Input)` /* ... */ `;
const ResendLink = styled.a<{ disabled?: boolean }>`
  color: ${props => props.disabled ? '#999' : '#000'};
  text-decoration: underline;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`;

const EditContainer = styled.div`
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 15px;
  font-size: 15px;
  color: #555;
  margin-top: 10px;
`;

const ButtonsContainer = styled.div`
  margin-top: 5px;
  width: 100%;
`;


const EditLink = styled.button`
  background: none;
  border: none;
  color:rgb(0, 0, 0);
  text-decoration: underline;
  cursor: pointer;
  font-size: 15px;
  padding: 0;
`;

const EditInputContainer = styled.div`
    width: 100%;
    max-width: 350px; /* Adjust as needed */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
`;

const EditButtonContainer = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 5px;
    width: 100%;
`;

const VerifyEmailScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const auth = getAuth();
  
  // Use state for details that might change
  const [userId, setUserId] = useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = useState<string>('');

  const [emailOtp, setEmailOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Set initial state from navigation
  useEffect(() => {
    const state = location.state as { userId: string; email: string } | null;
    if (state?.userId && state?.email) {
      setUserId(state.userId);
      setCurrentEmail(state.email);
      setNewEmail(state.email); // Initialize edit field
      startResendCooldownTimer(); // Start timer only after state is confirmed
    } else {
      console.error("Verification state missing, redirecting to register");
      navigate('/register');
    }

    return () => { // Cleanup timer on unmount
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [location.state, navigate]);


  const startResendCooldownTimer = () => {
    setResendCooldown(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
     if (value.length <= 6) setEmailOtp(value);
     setError('');
  };

  const handleNewEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(e.target.value);
    setError('');
  }

  const handleToggleEditEmail = () => {
      setIsEditingEmail(!isEditingEmail);
      setNewEmail(currentEmail); // Reset edit field on toggle
      setError(''); // Clear errors when toggling edit mode
  };

  const handleSaveNewEmail = async () => {
      setError('');
      if (!userId || !newEmail || newEmail === currentEmail) {
          setIsEditingEmail(false); // Just close if no change or invalid state
          return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
          setError('Please enter a valid email address.');
          return;
      }

      setIsSavingEmail(true);
      try {
          await updateUnverifiedEmail({ userId, newEmail });
          setCurrentEmail(newEmail); // Update displayed email
          setIsEditingEmail(false);
          setEmailOtp(''); // Clear OTP input as a new one was sent
          startResendCooldownTimer(); // Restart cooldown
          // Optional: Add a temporary success message "Email updated, new code sent"
      } catch (error: any) {
          console.error("Update email error:", error);
          if (error.message.includes('EMAIL_EXISTS')) {
              setError('This email address is already in use.');
          } else {
              setError('Failed to update email. Please try again.');
          }
      } finally {
          setIsSavingEmail(false);
      }
  };


  const handleVerifyEmailOtp = async () => {
      setError('');
      if (!emailOtp || emailOtp.length !== 6 || !userId) {
          setError('Please enter the 6-digit verification code');
          return;
      }
      
      setLoading(true);
      try {
          console.log("Verifying email OTP...");
          // Verify the OTP and get back custom auth token
          const result = await verifyOtp({ type: 'email', userId, otp: emailOtp });
          console.log("OTP verification result:", result);
          
          if (timerRef.current) clearInterval(timerRef.current);
          
          // Sign in the user with the custom token if available
          if (result.customToken || result.token) {
              try {
                  // Use the token from the response (either customToken or token field)
                  const tokenToUse = result.customToken || result.token;
                  console.log("Using custom token for authentication");
                  
                  // Sign in with Firebase using the custom token
                  const userCredential = await signInWithCustomToken(auth, tokenToUse);
                  console.log("Successfully signed in with custom token");
                  
                  // Set the user in the auth context
                  if (userCredential.user) {
                      console.log("User signed in successfully, UID:", userCredential.user.uid);
                      
                      // Force reload to get fresh token and user data
                      await userCredential.user.reload();
                      
                      // Prepare user data with all available fields from the response
                      const userData = {
                          id: userCredential.user.uid,
                          email: userCredential.user.email || result.user?.email || currentEmail,
                          emailVerified: true,
                          firstName: result.user?.firstName || '',
                          lastName: result.user?.lastName || '',
                          phoneNumber: result.user?.phoneNumber || '',
                          isVerified: result.user?.isVerified || true, // User is verified since email verification succeeded
                          phoneVerified: result.user?.phoneVerified || false
                      };
                      
                      // Update auth context with user data
                      setUser(userData);
                      console.log("Auth context updated with user data:", userData);
                      
                      // Store user data in localStorage for auth context to use
                      localStorage.setItem('userData', JSON.stringify(userData));
                      console.log("User data stored in localStorage");
                  }
              } catch (signInError) {
                  console.error("Error signing in with custom token:", signInError);
                  // Continue even if sign-in fails but log the error
              }
          } else {
              console.warn("No custom token received from server");
          }
          
          // Add a small delay to ensure authentication is complete
          console.log("Waiting for authentication to complete...");
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Go directly to profile picture upload
          console.log("Navigating to upload profile picture screen");
          navigate('/upload-profile-picture', {
              state: { required: true, fromRegistration: true }
          });
      } catch (error: any) {
          console.error("Verify OTP error:", error);
          if (error.message?.includes('OTP_EXPIRED')) {
              setError('Verification code expired. Please request a new one.');
          } else if (error.message?.includes('INVALID_OTP')) {
              setError('Invalid verification code. Please try again.');
          } else if (error.message?.includes('EMAIL_NOT_VERIFIED')) {
              setError('Email not verified. Please check your inbox or spam folder.');
          } else {
              setError('An unexpected error occurred. Please try again.');
          }
      }
      finally {
          setLoading(false);
      }
  };

  const handleResendEmailOtp = async () => {
       setError('');
       if (!userId || resendCooldown > 0 || isEditingEmail) return; // Don't resend while editing
       setLoading(true);
       try {
           // Explicitly send to the current email address
           await resendOtp({ 
             type: 'email', 
             userId, 
             email: currentEmail, // Pass the current email to ensure it's sent there
             resendSame: true // Flag to request sending the same OTP code again
           });
           
           // Show success feedback
           const successMessage = `Verification code resent to ${currentEmail}`;
           setError(`✓ ${successMessage}`); // Use error state to show success with checkmark
           console.log(successMessage);
           
           startResendCooldownTimer();
       } catch (error: any) { 
            console.error("Resend OTP error:", error);
            if (error.message?.includes('EMAIL_NOT_VERIFIED')) {
                setError('Email not verified. Please check your inbox or spam folder.');
            } else if (error.message?.includes('OTP_SEND_FAILED')) {
                setError('Failed to send verification code. Please try again later.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        }
       finally { setLoading(false); }
   };   if (!userId || !currentEmail) return <div>Loading...</div>;

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Verify Email</title>
        <meta name="description" content={`${APP_NAME} - Verify Email`} />
      </Helmet>
      <AuthLayout imageUrl="/assets/otp-image.jpg" altText="Verify Email">
      <Title>Verify Your Email</Title>

      {!isEditingEmail ? (
        <>
          <OtpInputContainer>
             <Input
                name="emailOtp" type="text"
                value={emailOtp} onChange={handleOtpChange} placeholder="######"
                label="Email Verification Code" required
             />
             {error && <ErrorMessage isSuccess={error.startsWith('✓')}>{error}</ErrorMessage>}
             <ButtonsContainer>
              <Button
                text="Verify Email" onClick={handleVerifyEmailOtp} loading={loading}
                disabled={loading || isSavingEmail || emailOtp.length !== 6} width="100%"
              />

            </ButtonsContainer>

          </OtpInputContainer>
          <EditContainer>
            Code sent to <strong>{currentEmail}</strong>, click <EditLink onClick={handleToggleEditEmail} disabled={loading}> here </EditLink> to change email.
          </EditContainer>
                        <ResendLink
                onClick={handleResendEmailOtp}
                disabled={loading || isSavingEmail || resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend'}
              </ResendLink>
        </>
      ) : (
        <>
           <Subtitle>Enter the new email address</Subtitle>
           <EditInputContainer>
               <Input
                  name="newEmail" type="email" value={newEmail}
                  onChange={handleNewEmailChange} placeholder="New email address"
                  label="New Email Address" required
               />
               {error && <ErrorMessage isSuccess={error.startsWith('✓')}>{error}</ErrorMessage>}
               <EditButtonContainer>
                   <Button
                      text="Save" onClick={handleSaveNewEmail} width='100%'
                      loading={isSavingEmail} disabled={isSavingEmail || !newEmail || newEmail === currentEmail}
                    />
                   <Button
                      text="Cancel" onClick={handleToggleEditEmail} width='100%'
                      disabled={isSavingEmail}
                    />
               </EditButtonContainer>
           </EditInputContainer>
        </>
      )}       <InfoText><StyledLink to="/login">Cancel and go to Login</StyledLink></InfoText>
    </AuthLayout>
    </>
  );
};

export default VerifyEmailScreen;